
import { supabase } from "@/integrations/supabase/client";
import { sendStaffNotification } from "@/services/notification/notificationService";
import { AttendanceType } from "@/models/attendanceModel";

interface StoreManager {
  email: string;
  first_name: string;
  last_name: string;
}

// Get store manager details
export const getStoreManager = async (storeId: string): Promise<StoreManager | null> => {
  try {
    // First, get the store owner
    const { data: store, error: storeError } = await supabase
      .from("stores")
      .select("owner_id")
      .eq("id", storeId)
      .single();

    if (storeError || !store?.owner_id) {
      console.error("Error fetching store owner:", storeError);
      return null;
    }

    // Get the owner's details from users table
    const { data: manager, error: managerError } = await supabase
      .from("users")
      .select("email, first_name, last_name")
      .eq("id", store.owner_id)
      .single();

    if (managerError) {
      console.error("Error fetching manager details:", managerError);
      return null;
    }

    return manager;
  } catch (error) {
    console.error("Error in getStoreManager:", error);
    return null;
  }
};

// Send clock-in notification
export const sendClockInNotification = async (
  staffId: string,
  storeId: string,
  staffName: string,
  storeName: string,
  clockInTime: Date
) => {
  try {
    const manager = await getStoreManager(storeId);
    if (!manager) {
      console.warn("No manager found for store:", storeId);
      return;
    }

    await sendStaffNotification({
      staff_name: staffName,
      attendance_type: "Clock In",
      details: `${staffName} has clocked in at ${clockInTime.toLocaleTimeString()}`,
      store_name: storeName,
      store_id: storeId,
      manager_email: manager.email,
      dashboard_url: `${window.location.origin}/dashboard/attendance`,
    });

    console.log("Clock-in notification sent successfully");
  } catch (error) {
    console.error("Error sending clock-in notification:", error);
  }
};

// Send absence notification
export const sendAbsenceNotification = async (
  staffId: string,
  storeId: string,
  staffName: string,
  storeName: string,
  type: AttendanceType,
  startDate: Date,
  endDate: Date,
  description?: string
) => {
  try {
    const manager = await getStoreManager(storeId);
    if (!manager) {
      console.warn("No manager found for store:", storeId);
      return;
    }

    const typeLabel = type === "holiday" ? "Holiday" : type === "sick" ? "Sick Leave" : "Other Absence";
    const dateRange = startDate.toDateString() === endDate.toDateString() 
      ? startDate.toDateString()
      : `${startDate.toDateString()} - ${endDate.toDateString()}`;

    let details = `${staffName} has recorded ${typeLabel} for ${dateRange}`;
    if (description) {
      details += `. Reason: ${description}`;
    }

    await sendStaffNotification({
      staff_name: staffName,
      attendance_type: typeLabel,
      details,
      store_name: storeName,
      store_id: storeId,
      manager_email: manager.email,
      dashboard_url: `${window.location.origin}/dashboard/attendance`,
    });

    console.log(`${typeLabel} notification sent successfully`);
  } catch (error) {
    console.error(`Error sending ${type} notification:`, error);
  }
};
